import React, { useEffect, useState } from 'react';

const DEFAULT_MESSAGES = [
  'Fetching invoices...',
  'Aggregating revenue...',
  'Building your dashboard...',
  'Almost there...',
];

export function LoadingOverlay({ loading, messages = DEFAULT_MESSAGES }) {
  const activeMessages = messages.length > 0 ? messages : DEFAULT_MESSAGES;
  const [messageIndex, setMessageIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [fading, setFading] = useState(false);

  // Show on the same render when loading becomes true (no useEffect delay).
  const shouldRender = loading || mounted;

  useEffect(() => {
    if (loading) {
      setMounted(true);
      setFading(false);
      setMessageIndex(0);
      return undefined;
    }

    if (mounted) {
      setFading(true);
      const t = window.setTimeout(() => {
        setMounted(false);
        setFading(false);
      }, 200);
      return () => window.clearTimeout(t);
    }

    return undefined;
  }, [loading, mounted]);

  useEffect(() => {
    if (!loading || activeMessages.length <= 1) return undefined;
    const interval = window.setInterval(() => {
      setMessageIndex((i) => (i + 1) % activeMessages.length);
    }, 1200);
    return () => window.clearInterval(interval);
  }, [loading, activeMessages]);

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 z-40 flex flex-col items-center justify-center transition-opacity duration-400 ${
        fading ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ background: 'rgba(238,241,245,0.85)', backdropFilter: 'blur(2px)' }}
    >
      <div className="flex flex-col items-center gap-5">
        <div className="relative flex items-center justify-center">
          <span
            className="absolute h-16 w-16 rounded-full border-2 border-[#201C35] opacity-20 animate-ping"
            style={{ animationDuration: '1.4s' }}
          />
          <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-[#201C35]">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="3" y="11" width="3" height="6" rx="1" fill="white" opacity="0.6">
                <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" begin="0s" repeatCount="indefinite" />
              </rect>
              <rect x="8.5" y="7" width="3" height="10" rx="1" fill="white" opacity="0.8">
                <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" begin="0.2s" repeatCount="indefinite" />
              </rect>
              <rect x="14" y="3" width="3" height="14" rx="1" fill="white">
                <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" begin="0.4s" repeatCount="indefinite" />
              </rect>
            </svg>
          </span>
        </div>

        <div className="flex flex-col items-center gap-1.5">
          <span className="text-[13px] font-semibold text-[#201C35] tracking-wide">
            VENUE INTELLIGENCE
          </span>
          <span
            key={messageIndex}
            className="text-[12px] text-slate-500 animate-fade-in"
            style={{ animation: 'fadeIn 0.4s ease' }}
          >
            {activeMessages[messageIndex]}
          </span>
        </div>

        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-[#201C35]"
              style={{
                animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                opacity: 0.7,
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
      `}</style>
    </div>
  );
}
